/**
 * Schema registry for derived-node ref-rewriting (EID-47).
 *
 * Builds an `allSchemas` map keyed by short name (e.g. `node/world`),
 * matching the regex used by `walkReferences` in `x-reference.ts`.
 * The synthetic `derivedNodeSchema` is a `oneOf` of every node type the
 * cloned subtree could be — the walker uses the data's `nodeType`
 * discriminator to pick the right branch.
 *
 * Kept out of `derive.ts` so the resolver itself stays Solid- and
 * schemas-package-agnostic and remains easy to unit-test in isolation.
 */

import {
  common_schema,
  data_schema,
  document_schema,
  feature_schema,
  geojson_schema,
  grid_schema,
  gridded_schema,
  label_schema,
  layer_schema,
  menu_schema,
  panel_schema,
  plot_schema,
  scenegraph_schema,
  seasurface_schema,
  theme_schema,
  track_schema,
  wmts_schema,
  world_schema,
} from 'schemas';

import { EIDOS_SCHEMA_URL_RE } from './x-reference';
import type { JsonSchema } from './x-reference';

// vega_schema is intentionally excluded (EID-63): its $id
// (vega.github.io/.../vega-lite) never matches EIDOS_SCHEMA_URL_RE, so it could
// not contribute to derivedAllSchemas anyway, but statically importing it
// dragged the ~1.87 MB vega-lite JSON into the eager runtime entry via this
// module.
const REGISTERED: Record<string, unknown>[] = [
  common_schema,
  data_schema,
  document_schema,
  feature_schema,
  geojson_schema,
  grid_schema,
  gridded_schema,
  label_schema,
  layer_schema,
  menu_schema,
  panel_schema,
  plot_schema,
  scenegraph_schema,
  seasurface_schema,
  theme_schema,
  track_schema,
  wmts_schema,
  world_schema,
];

export const derivedAllSchemas: Record<string, JsonSchema> = (() => {
  const out: Record<string, JsonSchema> = {};
  for (const s of REGISTERED) {
    const id = (s as { $id?: string }).$id;
    if (typeof id !== 'string') continue;
    const m = id.match(EIDOS_SCHEMA_URL_RE);
    if (!m) continue;
    out[m[1]] = s as JsonSchema;
  }
  return out;
})();

/**
 * Synthetic schema describing "any node type that can appear inside a
 * container slot". Excludes `derived` (a derived source can't itself be
 * derived) and `panel` (panels live as siblings under the eidos root,
 * not inside a derived subtree's content).
 *
 * Worldlayer types are listed too: a derived node's source could be a
 * world, whose children include worldlayer subtypes. The walker picks
 * the right oneOf branch based on the data's `nodeType` / `layerType`
 * discriminator.
 */
export const derivedNodeSchema: JsonSchema = {
  // $refs come from the schemas' own $ids, so they carry the current version.
  oneOf: [
    world_schema,
    plot_schema,
    document_schema,
    grid_schema,
    menu_schema,
    feature_schema,
    gridded_schema,
    label_schema,
    scenegraph_schema,
    seasurface_schema,
    track_schema,
    wmts_schema,
  ].map((s) => ({ $ref: (s as { $id: string }).$id })),
};
